/** Browser entry point (M2.1). Mounts the router-driven app. */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import './ui/styles.css';
import { fetchRuntimeConfig } from './net/config-api';
import { initRum, recordFunnelStage, setRumIdentity } from './net/rum';
import { loadIdentity } from './net/auth';
import { configureVoiceBackend } from './voice/voice-config';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

// The prerender (#103, FR-15.2) bakes the LANDING markup into `index.html`, which the
// SPA-fallback also serves for every client route. On a non-landing route, drop that
// prerendered content before mounting so there's no flash of the landing hero under
// `/host` etc.; on `/` React re-renders the same landing cleanly over it.
if (window.location.pathname !== '/' && rootEl.firstChild) {
  rootEl.replaceChildren();
}

// RUM (EI-4 / #35, §3.4). Kick off in the background so first paint isn't gated by
// the config fetch. `/api/config.rum` may be null (local/dev/storeless) — then RUM is
// silently disabled; `initRum` also handles a missing SDK gracefully.
void fetchRuntimeConfig().then((config) => {
  // Select the backend voice adapters where the deployment advertises them (#93,
  // D-SEAM); absent/null → the browser speech adapters. Resets the factory singletons.
  configureVoiceBackend(config.voice ?? null);
  if (config.rum?.cloudwatch) {
    void initRum(config.rum.cloudwatch).then(() => {
      // Identity enrichment (#35): stamp the persisted player/user id onto ALL RUM data
      // for this session as soon as RUM is up. Covers returning guests + registered users
      // (and, via the full-document reload after sign-in, freshly registered ones). A
      // brand-new visitor with no identity yet is promoted later by the first funnel event
      // that carries a `playerId` (see `recordFunnelStage`). No-op when identity is absent.
      const identity = loadIdentity();
      if (identity) {
        setRumIdentity({ playerId: identity.playerId, isGuest: identity.isGuest });
      }
      // Adoption funnel head (#148, NFR-6.6): the anonymous first visit. Emitted once
      // per load after RUM inits so it shares the session with the auto-captured
      // errors/http telemetry. No-op when RUM is disabled.
      recordFunnelStage('landed');
    });
  }
});

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
